/**
 * API-Vertrag zwischen App und Server (Version 1).
 *
 * Das ist die stabile Schnittstelle: Die App kann komplett neu gestaltet werden,
 * solange sie diese Endpunkte benutzt. Regeln für Änderungen:
 *  - Neue Felder immer optional oder mit Standardwert.
 *  - Nie ein Feld umbenennen oder entfernen – stattdessen ein neues ergänzen.
 *  - Wirklich inkompatible Änderungen bekommen /api/v2/…
 */
import { z } from 'zod';
import {
  CareEvent,
  CareTask,
  EventSource,
  Id,
  IsoDate,
  Outcome,
  Photo,
  Plant,
  Room,
  Settings,
  Soil,
  Species,
  TaskType,
  TypeKey,
} from './model';

export const API_BASE = '/api/v1';

/** Alle Endpunkte an einer Stelle – App und Server importieren dieselben Pfade. */
export const ROUTES = {
  health: '/health',
  deviceRegister: '/device/register',
  deviceRecover: '/device/recover',
  state: '/state',
  sync: '/sync',
  settings: '/settings',
  rooms: '/rooms',
  room: '/rooms/:id',
  roomState: '/rooms/:id/state',
  plants: '/plants',
  plant: '/plants/:id',
  tasks: '/tasks',
  task: '/tasks/:id',
  taskTypes: '/task-types',
  taskType: '/task-types/:key',
  species: '/species',
  speciesOne: '/species/:key',
  events: '/events',
  event: '/events/:id',
  photos: '/photos',
  photo: '/photos/:id',
  photoFile: '/photos/:id/file',
  weather: '/weather',
  pushKey: '/push/key',
  pushSubscribe: '/push/subscribe',
  pushUnsubscribe: '/push/unsubscribe',
  pushTest: '/push/test',
  diagnostics: '/diagnostics',
  exportAll: '/export',
  importAll: '/import',
  links: '/links',
  linksRotate: '/links/rotate',
  sitter: '/sitter',
} as const;

// ── Geräte und Anmeldung ──────────────────────────────────────────────────

export const RegisterRequest = z.object({
  label: z.string().max(60).default('iPhone'),
});
export const RegisterResponse = z.object({
  deviceToken: z.string(),
  /** Nur beim ersten Gerät: zum Aufbewahren (Handywechsel). */
  recoveryCode: z.string().nullable(),
  householdId: Id,
});
export type RegisterResponse = z.infer<typeof RegisterResponse>;

export const RecoverRequest = z.object({
  recoveryCode: z.string().min(10).max(80),
  label: z.string().max(60).default('iPhone'),
});

// ── Ereignisse (Abhaken) ──────────────────────────────────────────────────

/** Neues Ereignis. Die App erzeugt die ID selbst → doppelte Übertragung schadet nicht. */
export const EventInput = z.object({
  id: Id,
  taskId: Id,
  outcome: Outcome,
  soil: Soil.nullable().default(null),
  at: IsoDate.optional(),
  source: EventSource.default('app'),
  note: z.string().max(1000).default(''),
  photoId: Id.nullable().default(null),
  /** Nur bei outcome = snoozed: wann nochmal erinnern. */
  snoozeUntil: IsoDate.nullable().default(null),
});
export type EventInput = z.infer<typeof EventInput>;

/** Offline gesammelte Änderungen, die beim nächsten Kontakt nachgeliefert werden. */
export const SyncOp = z.discriminatedUnion('op', [
  z.object({ op: z.literal('event.add'), data: EventInput }),
  z.object({ op: z.literal('event.delete'), id: Id }),
  z.object({ op: z.literal('room.state'), id: Id, window: Room.shape.window.optional(), curtain: Room.shape.curtain.optional() }),
]);
export type SyncOp = z.infer<typeof SyncOp>;

export const SyncRequest = z.object({
  ops: z.array(SyncOp).max(500),
});

// ── Pflanzen, Räume, Aufgaben ─────────────────────────────────────────────

export const PlantInput = Plant.omit({ id: true, createdAt: true, archivedAt: true }).partial({ slug: true }).extend({
  id: Id.optional(),
  /** Beim Anlegen: Standard-Aufgaben aus dem Steckbrief erzeugen. */
  createDefaultTasks: z.boolean().default(true),
});
export type PlantInput = z.input<typeof PlantInput>;

export const PlantPatch = Plant.omit({ id: true, createdAt: true }).partial();
export const RoomInput = Room.omit({ id: true }).extend({ id: Id.optional() });
export const RoomPatch = Room.omit({ id: true }).partial();
export const RoomStateInput = z.object({
  window: Room.shape.window.optional(),
  curtain: Room.shape.curtain.optional(),
});
export const TaskInput = CareTask.omit({ id: true, createdAt: true }).extend({ id: Id.optional() });
export const TaskPatch = CareTask.omit({ id: true, plantId: true, typeKey: true, createdAt: true }).partial();
export const TaskTypeInput = TaskType.omit({ builtin: true });
export const SpeciesInput = Species.omit({ builtin: true });
export const SettingsPatch = Settings.partial();

// ── Fotos ─────────────────────────────────────────────────────────────────

/** Maximale Fotogröße (Cloudflare D1 erlaubt 2 MB pro Zeile). */
export const MAX_PHOTO_BYTES = 1_800_000;

export const PhotoMeta = Photo.omit({ id: true }).extend({ id: Id });

// ── Push ──────────────────────────────────────────────────────────────────

export const PushSubscribeRequest = z.object({
  endpoint: z.url().max(1000),
  keys: z.object({ p256dh: z.string().min(20).max(200), auth: z.string().min(10).max(100) }),
  /** iOS-Version/Browser zur Diagnose. */
  userAgent: z.string().max(400).default(''),
});
export type PushSubscribeRequest = z.infer<typeof PushSubscribeRequest>;

export const PushStatus = z.object({
  subscriptions: z.number(),
  lastSentAt: IsoDate.nullable(),
  lastStatus: z.number().nullable(),
  lastError: z.string().nullable(),
});

// ── Diagnose ──────────────────────────────────────────────────────────────

export const Diagnostics = z.object({
  serverTime: IsoDate,
  version: z.string(),
  deviceLabel: z.string(),
  push: PushStatus,
  lastCronAt: IsoDate.nullable(),
  lastCronMs: z.number().nullable(),
  lastReminderAt: IsoDate.nullable(),
  weatherFetchedAt: IsoDate.nullable(),
  counts: z.object({ plants: z.number(), events: z.number(), photos: z.number() }),
});
export type Diagnostics = z.infer<typeof Diagnostics>;

// ── Links für Kurzbefehle, NFC und Kalender ───────────────────────────────

export const LinkInfo = z.object({
  /** Basis für Kurzbefehle: POST {base}/{pflanze}/{aufgabe} */
  shortcutBase: z.url(),
  /** Kalender-Abo (webcal://…) */
  calendarUrl: z.string(),
  /** Seite für den Pflanzen-Sitter. */
  sitterUrl: z.url(),
});
export type LinkInfo = z.infer<typeof LinkInfo>;

/** Antwort der Kurzbefehl-Schnittstelle (für Siri gut vorlesbar). */
export const ShortcutResult = z.object({
  ok: z.boolean(),
  say: z.string(),
});

// ── Export / Import ───────────────────────────────────────────────────────

export const ExportFile = z.object({
  format: z.literal('pflanzen-export'),
  version: z.literal(1),
  exportedAt: IsoDate,
  settings: Settings,
  rooms: z.array(Room),
  plants: z.array(Plant),
  taskTypes: z.array(TaskType),
  species: z.array(Species),
  tasks: z.array(CareTask),
  events: z.array(CareEvent),
  photos: z.array(Photo.extend({ dataBase64: z.string().optional() })),
});
export type ExportFile = z.infer<typeof ExportFile>;

/** Fehlerformat aller Endpunkte. */
export const ApiError = z.object({
  error: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiError>;

export { TypeKey };
